<template>
    <div class="w-full flex flex-col items-start relative">
        <div class="w-full mt-1 flex flex-row items-center relative">
            <div :class="config.description && !hideLateralDescriptionValue ? 'flex-1 mr-4 relative' : 'grow relative'">
                <label
                    :for="config.display_name"
                    class="label py-2 text-sm absolute font-medium top-[-2rem]"
                    :class="overrideLabelOpacity ? 'override-opacity' : ''">
                    {{ config.display_name }}
                </label>
                <!-- Using PrimeVue MultiSelect -->
                <MultiSelect
                    v-model="controlValue"
                    :options="options"
                    optionLabel="label"
                    :placeholder="config.placeholder || config.value.placeholder || 'Select options'"
                    :disabled="!editMode"
                    appendTo="body"
                    :multiple="true"
                    :maxSelectedLabels="2"
                    :selectedItemsLabel="selectedLabel" />
                <div v-if="errors.length" class="text-red-700 mt-1 text-xs">
                    <template v-for="error in errors" :key="error.$uid">
                        <span>* {{ error.$message }} </span>
                    </template>
                </div>
            </div>
            <div
                v-if="
                    hideLateralDescriptionValue &&
                    config.display_name?.trim().toLowerCase() !== config.description?.trim().toLowerCase() &&
                    config.description?.trim().split(' ').length > 1
                "
                class="relative flex flex-col items-center group ml-2">
                <Tooltip :tooltip-text="config.description" position="left" />
            </div>
            <div
                v-if="
                    config.description &&
                    !hideLateralDescriptionValue &&
                    config.display_name?.trim().toLowerCase() !== config.description?.trim().toLowerCase() &&
                    config.description?.trim().split(' ').length > 1
                "
                class="flex-1 p-3 bg-green-50 border-l-4 border-green-200 text-xs text-black rounded-md relative arrow-left"
                :class="errors.length ? 'mb-5' : ''">
                <span v-html="config.description"></span>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUpdated, onUnmounted } from 'vue';
import MultiSelect from 'primevue/multiselect';
import Tooltip from './Tooltip.vue';
import _ from 'lodash';

const props = defineProps({
    config: { type: Object, required: true },
    value: { type: Array, default: () => [] },
    editMode: { type: Boolean, default: true },
    overrideLabelOpacity: { type: Boolean, default: false },
    hideLateralDescription: { type: Boolean, default: true },
    errors: { type: Array, default: () => [] }
});
const emit = defineEmits(['input']);

// Ensure controlValue is always an array.
const controlValue = ref(Array.isArray(props.value) ? props.value : Array.isArray(props.config.value.default) ? props.config.value.default : []);

// Compute options from config.value.raw_values.
const options = computed(() => {
    if (Array.isArray(props.config.value.raw_values)) {
        return props.config.value.raw_values.map((opt) => (typeof opt === 'object' ? opt : { label: opt, value: opt }));
    }
    return [];
});

// Compute the selected label based on controlValue length.
const selectedLabel = computed(() => {
    const count = controlValue.value.length;
    return count === 0 ? '' : `${count} item${count > 1 ? 's' : ''} selected`;
});

// Setup hideLateralDescriptionValue similar to the original.
const hideLateralDescriptionValue = ref(props.hideLateralDescription);
const handleResize = () => {
    hideLateralDescriptionValue.value = props.hideLateralDescription ? props.hideLateralDescription : window.innerWidth <= 1024;
};

window.addEventListener('resize', handleResize);
onMounted(() => {
    handleResize();
});
onUpdated(() => {
    hideLateralDescriptionValue.value = props.hideLateralDescription ? props.hideLateralDescription : window.innerWidth <= 1024;
});
onUnmounted(() => {
    window.removeEventListener('resize', handleResize);
});

// Emit changes when controlValue is updated.
watch(controlValue, (newVal) => {
    emit('input', newVal);
});
</script>

<style scoped>
.label {
    font-weight: 500;
    margin-bottom: 0.25rem;
    display: block;
}
.arrow-left::before {
    content: '';
    position: absolute;
    left: -12px;
    top: 50%;
    transform: translateY(-50%);
    width: 0;
    height: 0;
    border-top: 18px solid transparent;
    border-bottom: 18px solid transparent;
    border-right: 12px solid #bbf7d0;
}
</style>
